import React, { createContext, useContext, useEffect, useState } from 'react';
import { loadPublicContent } from '../lib/publicContent';
import { COMPANY_INFO as DEFAULT_COMPANY_INFO, SERVICES as DEFAULT_SERVICES, NEWS as DEFAULT_NEWS } from '../data';
import { optimizeDataImages } from '../lib/imageCompression';

// Helper to sanitize terms & optimize heavy image URLs
const sanitizeText = (obj: any): any => {
  if (typeof obj === 'string') {
    return optimizeDataImages(obj.replace(/văng chống/gi, (match) => {
        if (match === 'văng chống') return 'chống văng';
        if (match === 'Văng chống') return 'Chống văng';
        if (match === 'VĂNG CHỐNG') return 'CHỐNG VĂNG';
        if (match === 'Văng Chống') return 'Chống Văng';
        return 'chống văng';
    }));
  } else if (Array.isArray(obj)) {
    return obj.map(item => sanitizeText(item));
  } else if (obj !== null && typeof obj === 'object') {
    const newObj: any = {};
    for (const key of Object.keys(obj)) {
      newObj[key] = sanitizeText(obj[key]);
    }
    return newObj;
  }
  return obj;
};

export interface PriceTier {
  id: string;
  title: string;
  price: string;
  unit: string;
  features: string[];
  isPopular?: boolean;
}

export interface SiteInfo {
  name: string;
  taxCode: string;
  director: string;
  address: string;
  phone: string;
  email: string;
  logoUrl?: string;
  zaloUrl?: string;
  facebookUrl?: string;
  ogImage?: string;
  aboutBanner?: string;
  baseUrl?: string;
  showTestimonials?: boolean;
  showCompanyProfile?: boolean;
  priceTiers?: PriceTier[];
  bannerSlots?: Record<string, boolean>;
  enableComments?: boolean;
  commentType?: 'firebase' | 'facebook' | 'both' | 'none';
  facebookCommentCode?: string;
  // Google Business Profile
  googleBusinessUrl?: string;
  businessHours?: string;
  googleMapsUrl?: string;
  serviceArea?: string[];
}

const DEFAULT_BANNERS = [
  { url: '/assets/banners/hero-ep-coc-v2-20261008.webp', mobileUrl: '/assets/banners/hero-ep-coc-mobile-v1-20261008.webp' },
  { url: '/assets/banners/hero-chong-lun-v2-20261008.webp', mobileUrl: '/assets/banners/hero-chong-lun-mobile-v1-20261008.webp' },
  { url: '/assets/banners/hero-coc-nhoi-v2-20261008.webp', mobileUrl: '/assets/banners/hero-coc-nhoi-mobile-v1-20261008.webp' }
];

interface SiteContextType {
  siteInfo: SiteInfo;
  categories: any[];
  services: any[];
  banners: any[];
  gallery: any[];
  latestPosts: any[];
  loading: boolean;
}

const SiteContext = createContext<SiteContextType | undefined>(undefined);

export const SiteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [siteInfo, setSiteInfo] = useState<SiteInfo>(DEFAULT_COMPANY_INFO as SiteInfo);
  const [categories, setCategories] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>(DEFAULT_SERVICES);
  const [banners, setBanners] = useState<any[]>(DEFAULT_BANNERS);
  const [gallery, setGallery] = useState<any[]>([]);
  const [latestPosts, setLatestPosts] = useState<any[]>(DEFAULT_NEWS);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    loadPublicContent().then(({collections:c}) => {
      if (cancelled) return;
      const info = c.settings.find((s:any) => s.id === 'siteInfo');
      setSiteInfo({...DEFAULT_COMPANY_INFO,...sanitizeText(info||{}),baseUrl:window.location.origin});
      setCategories(c.categories.sort((a:any,b:any)=>(a.order||0)-(b.order||0)));
      setServices(optimizeDataImages(c.services));
      setBanners(c.banners.sort((a:any,b:any)=>(a.order||0)-(b.order||0)).map((b:any)=>({url:b.imageUrl,mobileUrl:b.mobileImageUrl||'',title:b.title||''})));
      setGallery(optimizeDataImages(c.gallery));
      setLatestPosts(optimizeDataImages(c.posts.sort((a:any,b:any)=>Date.parse(b.date)-Date.parse(a.date)).slice(0,10)));
    }).catch(error => console.error('Public content:',error)).finally(()=> {if(!cancelled) setLoading(false);});
    return ()=> {cancelled=true;};
  }, []);

  return (
    <SiteContext.Provider value={{ siteInfo, categories, services, banners, gallery, latestPosts, loading }}>
      {children}
    </SiteContext.Provider>
  );
};

export const useSiteInfo = () => {
  const context = useContext(SiteContext);
  if (context === undefined) {
    throw new Error('useSiteInfo must be used within a SiteProvider');
  }
  return context;
};
